<script setup>
// NOTICE: KAPTION-INTEGRATION - Added Kaption translation support for email messages
import { computed, useTemplateRef, ref, onMounted, watch } from 'vue';
import { Letter } from 'vue-letter';
import { sanitizeTextForRender } from '@chatwoot/utils';
import { allowedCssProperties } from 'lettersanitizer';

import Icon from 'next/icon/Icon.vue';
import { EmailQuoteExtractor } from './removeReply.js';
import BaseBubble from 'next/message/bubbles/Base.vue';
import FormattedContent from 'next/message/bubbles/Text/FormattedContent.vue';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import EmailMeta from './EmailMeta.vue';
import TranslationToggle from 'dashboard/components-next/message/TranslationToggle.vue';

import { useMessageContext } from '../../provider.js';
import { MESSAGE_TYPES } from 'next/message/constants.js';
import { useTranslations } from 'dashboard/composables/useTranslations';
// NOTICE: KAPTION-INTEGRATION - Import composables for Kaption translations
import { useUserLanguage } from 'dashboard/composables/useUserLanguage';
import { useAccount } from 'dashboard/composables/useAccount';

const {
  content,
  contentAttributes,
  attachments,
  messageType,
  // NOTICE: KAPTION-INTEGRATION - These are available if needed for future enhancements
  // id, conversationId, isMessageTranslating - currently unused but available via useMessageContext()
} = useMessageContext();

const isExpandable = ref(false);
const isExpanded = ref(false);
const showQuotedMessage = ref(false);
const renderOriginal = ref(false);
const contentContainer = useTemplateRef('contentContainer');

// NOTICE: KAPTION-INTEGRATION - Composables for user language detection
const { userLanguage } = useUserLanguage();
const { currentAccount } = useAccount();

onMounted(() => {
  isExpandable.value = contentContainer.value?.scrollHeight > 400;
});

// NOTICE: KAPTION-INTEGRATION - Watch contentAttributes for ActionCable updates
watch(
  () => contentAttributes.value,
  () => {
    // Content attributes changed - Vue will re-render
  },
  { deep: true }
);

const isOutgoing = computed(() => messageType.value === MESSAGE_TYPES.OUTGOING);
const isIncoming = computed(() => !isOutgoing.value);

const { hasTranslations, translationContent } =
  useTranslations(contentAttributes);

// NOTICE: KAPTION-INTEGRATION - Get the message's source language from translation metadata
const messageSourceLanguage = computed(() => {
  const attrs = contentAttributes.value;
  if (!attrs) return null;

  // Check old format first
  const oldTranslation = attrs.kaptionTranslation;
  if (oldTranslation) {
    const sourceLang =
      oldTranslation.sourceLanguage || oldTranslation.source_language;
    if (sourceLang && sourceLang !== 'unknown' && sourceLang !== 'Unknown') {
      return sourceLang;
    }
  }

  // Check new format (multiple translations)
  const translations = attrs.kaptionTranslations;
  if (translations && typeof translations === 'object') {
    const translationKeys = Object.keys(translations);
    for (let i = 0; i < translationKeys.length; i += 1) {
      const lang = translationKeys[i];
      const translation = translations[lang];
      const sourceLang =
        translation?.sourceLanguage || translation?.source_language;
      if (sourceLang && sourceLang !== 'unknown' && sourceLang !== 'Unknown') {
        return sourceLang;
      }
    }
  }

  return null;
});

// NOTICE: KAPTION-INTEGRATION - Get Kaption translation for user's language
const kaptionTranslation = computed(() => {
  const attrs = contentAttributes.value;
  if (!attrs) return null;

  const multiLangTranslations = attrs?.kaptionTranslations;
  const singleTranslation = attrs?.kaptionTranslation;

  const userLang = userLanguage?.value;
  const sourceLang = messageSourceLanguage.value;

  // If message is already in user's language, don't show translation
  if (sourceLang && userLang && sourceLang === userLang) {
    return null;
  }

  // Check new format first (multiple translations)
  if (multiLangTranslations && typeof multiLangTranslations === 'object') {
    // Try user's language first
    if (userLang && multiLangTranslations[userLang]) {
      return multiLangTranslations[userLang];
    }

    // Fallback to account locale
    const locale = currentAccount?.value?.locale;
    if (locale && typeof locale === 'string') {
      const accountLocale = locale.split('_')[0]?.toLowerCase?.();
      if (accountLocale && multiLangTranslations[accountLocale]) {
        return multiLangTranslations[accountLocale];
      }
    }

    return null;
  }

  // Fallback to old format
  return singleTranslation || null;
});

// NOTICE: KAPTION-INTEGRATION - Check if Kaption translation is available
const hasKaptionTranslation = computed(() => {
  return !!kaptionTranslation.value?.text;
});

// NOTICE: KAPTION-INTEGRATION - Get translated text
const kaptionTranslatedText = computed(() => {
  return kaptionTranslation.value?.text || '';
});

const originalEmailText = computed(() => {
  const text =
    contentAttributes?.value?.email?.textContent?.full ?? content.value;
  return sanitizeTextForRender(text);
});

const originalEmailHtml = computed(
  () =>
    contentAttributes?.value?.email?.htmlContent?.full ||
    originalEmailText.value
);

const messageContent = computed(() => {
  // If translations exist and we're showing translations (not original)
  if (hasTranslations.value && !renderOriginal.value) {
    return translationContent.value;
  }
  // Otherwise show original content
  return content.value;
});

const textToShow = computed(() => {
  // If translations exist and we're showing translations (not original)
  if (hasTranslations.value && !renderOriginal.value) {
    return translationContent.value;
  }
  // Otherwise show original text
  return originalEmailText.value;
});

const fullHTML = computed(() => {
  // If translations exist and we're showing translations (not original)
  if (hasTranslations.value && !renderOriginal.value) {
    return translationContent.value;
  }
  // Otherwise show original HTML
  return originalEmailHtml.value;
});

const unquotedHTML = computed(() =>
  EmailQuoteExtractor.extractQuotes(fullHTML.value)
);

// NOTICE: KAPTION-INTEGRATION - Unquoted HTML for original display (when showing translation)
const originalUnquotedHTML = computed(() =>
  EmailQuoteExtractor.extractQuotes(originalEmailHtml.value)
);

const hasQuotedMessage = computed(() =>
  EmailQuoteExtractor.hasQuotes(fullHTML.value)
);

// Ensure unique keys for <Letter> when toggling between original and translated views.
// This forces Vue to re-render the component and update content correctly.
const translationKeySuffix = computed(() => {
  if (renderOriginal.value) return 'original';
  if (hasTranslations.value) return 'translated';
  if (hasKaptionTranslation.value) return 'kaption-translated';
  return 'original';
});

const handleSeeOriginal = () => {
  renderOriginal.value = !renderOriginal.value;
};
</script>

<template>
  <BaseBubble
    class="w-full"
    :class="{
      'bg-n-slate-4': isIncoming,
      'bg-n-solid-blue': isOutgoing,
    }"
    data-bubble-name="email"
  >
    <EmailMeta
      class="p-3"
      :class="{
        'border-b border-n-strong': isIncoming,
        'border-b border-n-slate-8/20': isOutgoing,
      }"
    />
    <section ref="contentContainer" class="p-3">
      <div
        :class="{
          'max-h-[400px] overflow-hidden relative': !isExpanded && isExpandable,
          'overflow-y-scroll relative': isExpanded,
        }"
      >
        <div
          v-if="isExpandable && !isExpanded"
          class="absolute left-0 right-0 bottom-0 h-40 px-8 flex items-end"
          :class="{
            'bg-gradient-to-t from-n-slate-4 via-n-slate-4 via-20% to-transparent':
              isIncoming,
            'bg-gradient-to-t from-n-solid-blue via-n-solid-blue via-20% to-transparent':
              isOutgoing,
          }"
        >
          <button
            class="text-n-slate-12 py-2 px-8 mx-auto text-center flex items-center gap-2"
            @click="isExpanded = true"
          >
            <Icon icon="i-lucide-maximize-2" />
            {{ $t('EMAIL_HEADER.EXPAND') }}
          </button>
        </div>

        <!-- NOTICE: KAPTION-INTEGRATION - Kaption translation display (double text format) -->
        <template v-if="hasKaptionTranslation && !renderOriginal">
          <!-- Original email content (small, on top) -->
          <div class="kaption-original-small mb-2">
            <Letter
              v-if="isIncoming"
              :key="`letter-original-small-${translationKeySuffix}`"
              class-name="prose prose-bubble prose-sm !max-w-none letter-render opacity-60 !text-xs [&_p]:!text-xs [&_div]:!text-xs"
              :allowed-css-properties="[
                ...allowedCssProperties,
                'transform',
                'transform-origin',
              ]"
              :html="originalUnquotedHTML"
              :text="originalEmailText"
            />
            <FormattedContent
              v-else
              class="text-n-slate-12 opacity-60 !text-xs [&_p]:!text-xs"
              :content="content"
            />
          </div>
          <!-- Translated content (normal size, below) -->
          <FormattedContent
            class="text-n-slate-12"
            :content="kaptionTranslatedText"
          />
        </template>

        <!-- Default display (no Kaption translation or showing original) -->
        <template v-else>
          <FormattedContent
            v-if="isOutgoing && content"
            class="text-n-slate-12"
            :content="messageContent"
          />
          <template v-else>
            <Letter
              v-if="showQuotedMessage"
              :key="`letter-quoted-${translationKeySuffix}`"
              class-name="prose prose-bubble !max-w-none letter-render"
              :allowed-css-properties="[
                ...allowedCssProperties,
                'transform',
                'transform-origin',
              ]"
              :html="fullHTML"
              :text="textToShow"
            />
            <Letter
              v-else
              :key="`letter-unquoted-${translationKeySuffix}`"
              class-name="prose prose-bubble !max-w-none letter-render"
              :html="unquotedHTML"
              :allowed-css-properties="[
                ...allowedCssProperties,
                'transform',
                'transform-origin',
              ]"
              :text="textToShow"
            />
          </template>
        </template>

        <button
          v-if="hasQuotedMessage && !hasKaptionTranslation"
          class="text-n-slate-11 px-1 leading-none text-sm bg-n-alpha-black2 text-center flex items-center gap-1 mt-2"
          @click="showQuotedMessage = !showQuotedMessage"
        >
          <template v-if="showQuotedMessage">
            {{ $t('CHAT_LIST.HIDE_QUOTED_TEXT') }}
          </template>
          <template v-else>
            {{ $t('CHAT_LIST.SHOW_QUOTED_TEXT') }}
          </template>
          <Icon
            :icon="
              showQuotedMessage
                ? 'i-lucide-chevron-up'
                : 'i-lucide-chevron-down'
            "
          />
        </button>
      </div>
    </section>
    <!-- NOTICE: KAPTION-INTEGRATION - Only show toggle for generic translations, not Kaption -->
    <!-- Kaption translations show both original and translated content simultaneously -->
    <TranslationToggle
      v-if="hasTranslations && !hasKaptionTranslation"
      class="py-2 px-3"
      :showing-original="renderOriginal"
      @toggle="handleSeeOriginal"
    />
    <section
      v-if="Array.isArray(attachments) && attachments.length"
      class="px-4 pb-4 space-y-2"
    >
      <AttachmentChips :attachments="attachments" class="gap-1" />
    </section>
  </BaseBubble>
</template>

<style lang="scss">
// Tailwind resets break the rendering of google drive link in Gmail messages
// This fixes it using https://developer.mozilla.org/en-US/docs/Web/CSS/Attribute_selectors

.letter-render [class*='gmail_drive_chip'] {
  box-sizing: initial;
  @apply bg-n-slate-4 border-n-slate-6 rounded-md !important;

  a {
    @apply text-n-slate-12 !important;

    img {
      display: inline-block;
    }
  }
}
</style>
